/**
 * Hooks de Terminal. CRUD nested bajo /branches/{id}/terminals/.
 *
 * Lectura disponible para todos los usuarios autenticados (la PWA del cajero
 * lo necesita para el selector). Las mutaciones requieren rol admin.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api.ts";
import type {
  TerminalCreateInput,
  TerminalOut,
  TerminalUpdateInput,
} from "@/types/api.ts";

export const terminalKeys = {
  all: ["terminals"] as const,
  byBranch: (branchId: string) => [...terminalKeys.all, "branch", branchId] as const,
  byBranchActive: (branchId: string) =>
    [...terminalKeys.all, "branch", branchId, "active"] as const,
};

export function useBranchTerminals(
  branchId: string | undefined | null,
  opts?: { onlyActive?: boolean },
) {
  const onlyActive = opts?.onlyActive ?? false;
  return useQuery({
    queryKey: onlyActive
      ? terminalKeys.byBranchActive(branchId ?? "")
      : terminalKeys.byBranch(branchId ?? ""),
    queryFn: () =>
      api.get<TerminalOut[]>(`/branches/${branchId}/terminals`, {
        searchParams: { only_active: onlyActive },
      }),
    enabled: !!branchId,
    staleTime: 30_000,
  });
}

export function useCreateTerminal(branchId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TerminalCreateInput) =>
      api.post<TerminalOut>(`/branches/${branchId}/terminals`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: terminalKeys.byBranch(branchId) });
      qc.invalidateQueries({ queryKey: terminalKeys.byBranchActive(branchId) });
    },
  });
}

export function useUpdateTerminal(branchId: string, terminalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TerminalUpdateInput) =>
      api.patch<TerminalOut>(`/branches/${branchId}/terminals/${terminalId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: terminalKeys.byBranch(branchId) });
      qc.invalidateQueries({ queryKey: terminalKeys.byBranchActive(branchId) });
    },
  });
}

export function useDeleteTerminal(branchId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (terminalId: string) =>
      api.del<void>(`/branches/${branchId}/terminals/${terminalId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: terminalKeys.byBranch(branchId) });
      qc.invalidateQueries({ queryKey: terminalKeys.byBranchActive(branchId) });
    },
  });
}
